import {
  BackOfficeAccount,
  BackOfficeLocation,
  CommodityClassesResponse,
  AdditionalServicesResponse,
} from "../../hooks/react-query/backOffice/useQuickQuote";

type ApiResponseWrapper<T> = {
  success: boolean;
  message: string | null;
  errorCode: string | null;
  item: T[];
};

export const mockBackOfficeAccounts = {
  empty: {
    success: true,
    message: null,
    errorCode: null,
    item: [],
  },
  default: {
    success: true,
    message: null,
    errorCode: null,
    item: [
      {
        id: 1,
        accountNumber: "AB2812",
        accountName: "Account AB2812",
      },
      {
        id: 2,
        accountNumber: "AB2",
        accountName: "Account AB2",
      },
      {
        id: 3,
        accountNumber: "AB3",
        accountName: "Account AB3",
      },
      {
        id: 4,
        accountNumber: "AB4",
        accountName: "Account AB4",
      },
    ],
  },
} as const satisfies Record<string, ApiResponseWrapper<BackOfficeAccount>>;

export const mockBackOfficeLocations = {
  empty: {
    success: true,
    message: null,
    errorCode: null,
    item: [],
  },
  default: {
    success: true,
    message: null,
    errorCode: null,
    item: [
      {
        id: 820902,
        city: "STEILACOOM",
        state: "WA",
        zip: "RCAR",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 820946,
        city: "STEILACOOM",
        state: "WA",
        zip: "RCAEMOTE",
        country: "United States",
        shortCountry: "US",
      },
      {
        id: 254893,
        city: "STEILACOOM",
        state: "WA",
        zip: "RCA1",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 256505,
        city: "STEILACOOM",
        state: "WA",
        zip: "RCA0",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 1003188,
        city: "STEILACOOM",
        state: "WA",
        zip: "RCA2",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 1003189,
        city: "New York",
        state: "NY",
        zip: "10001",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 1003190,
        city: "Los Angeles",
        state: "CA",
        zip: "90210",
        country: "United States",
        shortCountry: "USA",
      },
      {
        id: 1003191,
        city: "Chicago",
        state: "IL",
        zip: "60601",
        country: "United States",
        shortCountry: "USA",
      },
    ],
  },
} as const satisfies Record<string, ApiResponseWrapper<BackOfficeLocation>>;

export const mockCommodityClassOptions = {
  empty: {
    success: true,
    message: null,
    errorCode: null,
    item: [],
  },
  default: {
    success: true,
    message: null,
    errorCode: null,
    item: [
      {
        id: 1,
        description: "Tariff Class 10",
        actualClass: 10,
        commodityCode: "10",
      },
      {
        id: 2,
        description: "Tariff Class 20",
        actualClass: 20,
        commodityCode: "20",
      },
      {
        id: 3,
        description: "Tariff Class 30",
        actualClass: 30,
        commodityCode: "30",
      },
      {
        id: 4,
        description: "Tariff Class 40",
        actualClass: 40,
        commodityCode: "40",
      },
      {
        id: 5,
        description: "Tariff Class 50",
        actualClass: 50,
        commodityCode: "50",
      },
      {
        id: 6,
        description: "Tariff Class 55",
        actualClass: 55,
        commodityCode: "55",
      },
      {
        id: 7,
        description: "Tariff Class 60",
        actualClass: 60,
        commodityCode: "60",
      },
      {
        id: 8,
        description: "Tariff Class 65",
        actualClass: 65,
        commodityCode: "65",
      },
      {
        id: 9,
        description: "Tariff Class 70",
        actualClass: 70,
        commodityCode: "70",
      },
      {
        id: 10,
        description: "Tariff Class 77",
        actualClass: 77,
        commodityCode: "77",
      },
      {
        id: 11,
        description: "Tariff Class 85",
        actualClass: 85,
        commodityCode: "85",
      },
      {
        id: 12,
        description: "Tariff Class 92",
        actualClass: 92,
        commodityCode: "92",
      },
      {
        id: 13,
        description: "Tariff Class 100",
        actualClass: 100,
        commodityCode: "100",
      },
      {
        id: 14,
        description: "Tariff Class 110",
        actualClass: 110,
        commodityCode: "110",
      },
      {
        id: 15,
        description: "Tariff Class 125",
        actualClass: 125,
        commodityCode: "125",
      },
      {
        id: 16,
        description: "Tariff Class 150",
        actualClass: 150,
        commodityCode: "150",
      },
      {
        id: 17,
        description: "Tariff Class 175",
        actualClass: 175,
        commodityCode: "175",
      },
      {
        id: 18,
        description: "Tariff Class 200",
        actualClass: 200,
        commodityCode: "200",
      },
      {
        id: 19,
        description: "Tariff Class 250",
        actualClass: 250,
        commodityCode: "250",
      },
      {
        id: 20,
        description: "Tariff Class 300",
        actualClass: 300,
        commodityCode: "300",
      },
      {
        id: 21,
        description: "Tariff Class 400",
        actualClass: 400,
        commodityCode: "400",
      },
      {
        id: 22,
        description: "Tariff Class 500",
        actualClass: 500,
        commodityCode: "500",
      },
    ],
  },
} as const satisfies Record<string, CommodityClassesResponse>;

export const mockAdditionalServices = {
  empty: {
    success: true,
    message: null,
    errorCode: null,
    item: {
      pickup: [],
      delivery: [],
    },
  },
  default: {
    success: true,
    message: null,
    errorCode: null,
    item: {
      pickup: [
        {
          id: 147,
          title: "Limited Quantity",
        },
        {
          id: 145,
          title: "Grocery Warehouse Pickup",
        },
        {
          id: 99,
          title: "Limited Access or Construction Site P/U",
        },
        {
          id: 12,
          title: "Standard Skid Non-Stackable",
        },
        {
          id: 9,
          title: "Oversized Skid Stackable",
        },
        {
          id: 8,
          title: "Oversized Skid Non-Stackable",
        },
        {
          id: 5,
          title: "Lift Gate Pickup Required",
        },
        {
          id: 4,
          title: "Inside Pickup Required",
        },
      ],
      delivery: [
        {
          id: 146,
          title: "Grocery Warehouse Delivery",
        },
        {
          id: 138,
          title: "Delivery Appointment Not Required",
        },
        {
          id: 128,
          title: "Lumper Required",
        },
        {
          id: 100,
          title: "Limited Access or Construction Site Del",
        },
        {
          id: 55,
          title: "Inside Delivery Required",
        },
        {
          id: 53,
          title: "Lift Gate Delivery Required",
        },
        {
          id: 24,
          title: "Packing List Attached",
        },
        {
          id: 23,
          title: "Delivery Appointment Required",
        },
        {
          id: 21,
          title: "Curbside/Dock Dlvry Only– No Ins Dlvry",
        },
        {
          id: 11,
          title: "Residential Delivery",
        },
      ],
    },
  },
} as const satisfies Record<string, AdditionalServicesResponse>;
